import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Grid,
  GridItem,
  Button,
  Circle,
  Badge,
  Flex,
  Image,
  useToast
} from '@chakra-ui/react';
import { FaCrown, FaCoins } from 'react-icons/fa';

/**
 * ScratchCardTemplate
 * 
 * A flexible template component that can render any scratch card configuration.
 * Supports both preview mode (for selection) and playable mode.
 */
const ScratchCardTemplate = ({ 
  cardConfig, 
  isPreview = false, 
  onPlay, 
  onReveal, 
  onBuy, 
  gameState = null 
}) => {
  const toast = useToast();
  const [revealed, setRevealed] = useState(isPreview ? true : false);
  const [cells, setCells] = useState(
    Array(cardConfig.gridSize.rows * cardConfig.gridSize.columns)
      .fill()
      .map((_, i) => ({ 
        id: i, 
        revealed: false, 
        value: null,
        isWinning: false
      }))
  );
  
  useEffect(() => {
    if (isPreview) return;
    
    // Get grid dimensions from card config
    const { rows, columns } = cardConfig.gridSize;
    const totalCells = rows * columns;
    
    // Create cells based on grid size
    const initialCells = Array(totalCells).fill().map(() => ({
      revealed: false,
      value: null,
      isWinning: false
    }));
    
    setCells(initialCells);
  }, [isPreview, cardConfig]);

  // Handle cell click (scratch)
  const handleCellClick = (index) => {
    if (isPreview || !gameState || gameState.status !== 'active') return;
    
    const newCells = [...cells];
    newCells[index].revealed = true;
    
    // In a real game, this would come from the backend
    // For now, we're just simulating
    if (!newCells[index].value) {
      newCells[index].value = gameState.cellValues[index];
      newCells[index].isWinning = gameState.winningCells.includes(index);
    }
    
    setCells(newCells);
    
    // Check if all cells are revealed
    const allRevealed = newCells.every(cell => cell.revealed);
    if (allRevealed && onReveal) {
      // Also reveal all prize values when all cells are revealed
      setRevealedPrizes([true, true, true]);
      onReveal(newCells);
    }
  };
  
  // Quick reveal all cells
  const handleRevealAll = () => {
    if (isPreview || !gameState || gameState.status !== 'active') return;
    
    const newCells = cells.map((cell, index) => ({
      ...cell,
      revealed: true,
      value: gameState.cellValues[index],
      isWinning: gameState.winningCells.includes(index)
    }));
    
    setCells(newCells);
    
    // Also reveal all prize values
    setRevealedPrizes([true, true, true]);
    
    if (onReveal) {
      onReveal(newCells);
    }
  };
  
  // Determine the winning symbol based on card type
  let winningSymbol = '⭐️';
  if (cardConfig.id === 'cash_king') {
    winningSymbol = '💎';
  } else if (cardConfig.id === 'jackpot_millions') {
    winningSymbol = '👑';
  } else if (cardConfig.id === 'quick_cash_premium') {
    winningSymbol = '💎';
  }

  // Render a cell
  const renderCell = (index) => {
    const cell = cells[index];
    const isRevealed = cell.revealed || isPreview;
    
    // Use the 777 jackpot symbol for all cells
    const cellSymbol = '777';
    
    return (
      <Box
        key={index}
        bg="#ffc107" // Bright yellow background for cells
        border="1px solid"
        borderColor="#e6a800"
        borderRadius="md"
        height={isPreview ? "80px" : "80px"}
        width={isPreview ? "80px" : "80px"}
        display="flex"
        alignItems="center"
        justifyContent="center"
        cursor={isPreview ? "default" : "pointer"}
        onClick={() => handleCellClick(index)}
        position="relative"
        transition="all 0.2s"
        _hover={!isPreview && { transform: "scale(1.05)" }}
        m={1}
      >
        {isRevealed ? (
          <Text 
            fontWeight="bold" 
            fontSize={isPreview ? "xs" : "xl"}
            color="black"
          >
            {isPreview ? cellSymbol : (cell.value || cellSymbol)}
          </Text>
        ) : (
          <Box 
            position="absolute" 
            top="0" 
            left="0" 
            right="0" 
            bottom="0" 
            bg="#ffc107"
            borderRadius="md"
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={1}
          >
            <Image src="/images/NFT/777.png" height="80%" />
          </Box>
        )}
      </Box>
    );
  };
  
  // Render the grid
  const renderGrid = () => {
    const { rows, columns } = cardConfig.gridSize;
    const totalCells = rows * columns;
    
    return (
      <Box
        bg="#0d2747" // Darker blue background for the grid container
        borderRadius="md"
        p={4}
        width="100%"
        maxW={isPreview ? "280px" : "300px"}
        mx="auto"
      >
        <Grid
          templateColumns={`repeat(${columns}, 1fr)`}
          templateRows={`repeat(${rows}, 1fr)`}
          gap={2}
          width="100%"
        >
          {Array(totalCells).fill().map((_, i) => (
            <GridItem key={i}>
              {renderCell(i)}
            </GridItem>
          ))}
        </Grid>
      </Box>
    );
  };
  
  // Use predefined whole number values for the winning symbols based on the card type
  const [prizeValues, setPrizeValues] = useState(() => {
    // Predefined prize values based on the card type
    const possiblePrizes = [
      '200', '300', '500', '1K', '2K', '5K', '10K', '50K'
    ];
    
    // Create a copy of the array to avoid modifying the original
    const availablePrizes = [...possiblePrizes];
    const selectedPrizes = [];
    
    // Select 3 different random prizes from the possible values
    for (let i = 0; i < 3; i++) {
      if (availablePrizes.length === 0) break;
      
      const randomIndex = Math.floor(Math.random() * availablePrizes.length);
      const selectedPrize = availablePrizes.splice(randomIndex, 1)[0];
      selectedPrizes.push(selectedPrize);
    }
    
    return selectedPrizes;
  });

  useEffect(() => {
    if (isPreview) return;
    
    // Get number of winning symbols from card config
    const numWinningSymbols = cardConfig?.winningSymbols || 3;
    
    // Set prize values based on number of winning symbols
    const newPrizeValues = Array(numWinningSymbols).fill().map(() => 
      cardConfig.prizes[Math.floor(Math.random() * cardConfig.prizes.length)]
    );
    setPrizeValues(newPrizeValues);
    
    // Initialize revealed prizes state
    setRevealedPrizes(Array(numWinningSymbols).fill(false));
  }, [isPreview, cardConfig]);

  // State to track which prize circles have been revealed
  const [revealedPrizes, setRevealedPrizes] = useState([false, false, false]);

  // Handle revealing a prize circle
  const handleRevealPrize = (index) => {
    if (isPreview) return;
    
    const newRevealed = [...revealedPrizes];
    newRevealed[index] = true;
    setRevealedPrizes(newRevealed);
  };

  // Reveal all prize circles
  useEffect(() => {
    // If all cells are revealed (via handleRevealAll), also reveal all prizes
    if (cells.every(cell => cell.revealed) && !isPreview) {
      // Get number of winning symbols from card config
      const numWinningSymbols = cardConfig?.winningSymbols || 3;
      setRevealedPrizes(Array(numWinningSymbols).fill(true));
    }
  }, [cells, isPreview, cardConfig]);

  // Get symbol character based on card config
  const getSymbolCharacter = () => {
    const symbolType = cardConfig?.symbolType || 'star';
    switch (symbolType) {
      case 'diamond': return '♦';
      case 'crown': return '♛';
      case 'star':
      default: return '★';
    }
  };

  // Render prize circles with dynamic winning symbols based on card config
  const renderPrizes = () => {
    // Get number of winning symbols from card config (default to 3)
    const numWinningSymbols = cardConfig?.winningSymbols || 3;
    const symbolChar = getSymbolCharacter();
    
    return (
      <VStack spacing={2} my={4}>
        <Flex justify="center" gap={4} flexWrap="wrap" maxW="100%">
          {Array(numWinningSymbols).fill().map((_, idx) => (
            <Circle 
              key={idx}
              size={isPreview ? "30px" : "65px"} 
              bg="blackAlpha.700"
              color={cardConfig?.accentColor || "yellow.300"}
              fontWeight="bold"
              border="2px solid"
              borderColor="whiteAlpha.400"
              fontSize={isPreview ? "xs" : "2xl"}
              cursor={isPreview ? "default" : "pointer"}
              onClick={() => handleRevealPrize(idx)}
              position="relative"
              overflow="hidden"
            >
              {isPreview || !revealedPrizes[idx] ? 
                symbolChar : 
                <Text fontSize="md" color="white">{prizeValues[idx] || '0'}</Text>
              }
            </Circle>
          ))}
        </Flex>
        <Text fontSize="sm" color={cardConfig?.accentColor || "yellow.300"} fontWeight="bold" mt={1}>
          Winning Symbols
        </Text>
      </VStack>
    );
  };
  
  // Get background color based on card type
  const getCardBgColor = () => {
    switch(cardConfig.id) {
      case 'quick_cash':
        return '#1a365d'; // Darker blue background for QUICK CASH
      case 'cash_king':
        return 'green.700'; // Green background for CASH KING
      case 'jackpot_millions':
        return 'purple.600'; // Purple background for JACKPOT MILLIONS
      case 'quick_cash_premium':
        return 'yellow.700'; // Gold background for QUICK CASH PREMIUM
      default:
        return '#1a365d';
    }
  };
  
  // Get the number of winning symbols required based on card type
  const getWinningSymbolsCount = () => {
    switch(cardConfig.id) {
      case 'quick_cash':
        return 3; // Match 3 symbols for QUICK CASH
      case 'cash_king':
        return 4; // Match 4 symbols for CASH KING
      case 'jackpot_millions':
        return 5; // Match 5 symbols for JACKPOT MILLIONS
      case 'quick_cash_premium':
        return 3; // Match 3 symbols for QUICK CASH PREMIUM
      default:
        return 3;
    }
  };

  // Render creator badge if it's a user-created game
  const renderCreatorBadge = () => {
    if (cardConfig.isOfficial) return null;
    
    return (
      <Box 
        position="absolute" 
        top="10px" 
        right="10px" 
        bg="rgba(0,0,0,0.7)" 
        color="purple.300" 
        px={2} 
        py={1} 
        borderRadius="md"
        fontWeight="bold"
        display="flex"
        alignItems="center"
        gap={1}
      >
        <FaCrown /> Creator: 20%
      </Box>
    );
  };
  
  return (
    <Box 
      position="relative" 
      bg={getCardBgColor()} 
      p={6} 
      borderRadius="lg" 
      boxShadow="xl"
      width={isPreview ? "240px" : "480px"}
      height={isPreview ? "auto" : "auto"}
      mx="0"
      display="flex"
      flexDirection="column"
      justifyContent="space-between"
    >
      {/* Cost badge */}
      <Box 
        position="absolute" 
        top="10px" 
        left="10px" 
        bg="rgba(0,0,0,0.7)" 
        color="yellow.300" 
        px={2} 
        py={1} 
        borderRadius="md"
        fontWeight="bold"
        display="flex"
        alignItems="center"
        gap={1}
      >
        <FaCoins /> {cardConfig.cost} JINU
      </Box>
      
      {/* Creator badge if applicable */}
      {renderCreatorBadge()}
      
      {/* Card header - Title and description */}
      <VStack spacing={1} mt={isPreview ? 4 : 6} mb={2}>
        <Heading 
          color="yellow.300" 
          fontWeight="bold" 
          fontSize={isPreview ? "3xl" : "3xl"} 
          textAlign="center"
        >
          {cardConfig.name}
        </Heading>
        <Text 
          color="white" 
          fontWeight="bold" 
          fontSize={isPreview ? "md" : "md"} 
          textAlign="center"
        >
          {cardConfig.description}
        </Text>
      </VStack>
      
      {/* Winning Symbols Display */}
      <Box textAlign="center" mt={4} mb={4}>
        <HStack justify="center" spacing={4}>
          {Array.from({ length: getWinningSymbolsCount() }).map((_, i) => (
            <Circle 
              key={i} 
              size="50px" 
              bg="#0d2747" 
              color="yellow.300"
              display="flex"
              alignItems="center"
              justifyContent="center"
              border="1px solid"
              borderColor="rgba(255, 255, 255, 0.2)"
            >
              <Text fontSize="2xl">⭐</Text>
            </Circle>
          ))}
        </HStack>
        <Text color="#4fd1c5" mt={2} fontSize="md" fontWeight="medium">Winning Symbols</Text>
      </Box>
      
      {/* Card grid - Made larger */}
      <Box my={3} bg="rgba(0,0,0,0.3)" p={3} borderRadius="md">
        {renderGrid()}
      </Box>
      
      {/* Match instructions */}
      <Text 
        color="white" 
        fontSize={isPreview ? "lg" : "lg"} 
        textAlign="center" 
        mt={6}
        mb={2}
        fontWeight="medium"
      >
        Match 3 identical symbols to win their prize value.
      </Text>
      
      {/* Action buttons removed as requested */}
    </Box>
  );
};

export default ScratchCardTemplate;
